import { RuntimeGS } from '../app/RuntimeGS';

/** @internal */
export interface TemplateConstructorGS<T extends object = object> {
    new(...args: any[]): T;
}

/** @internal */
export const templateMetadataGS = {
    templateName: 'tmpl',
    templateTag: 'tmpl-tag',
    controlName: 'ctrl'
};

interface TemplateDocumentContextGS {
    readonly templates: Map<string, HTMLElement>;
    initializedFromDocument: boolean;
    buildId?: string;
}

const templateConstructors = new Map<string, TemplateConstructorGS>();
const templateContexts = new WeakMap<Document, TemplateDocumentContextGS>();

function normalizeTemplateName(name: string | undefined): string {
    return (name ?? '').trim().toLowerCase();
}

function getTemplateContext(targetDocument: Document): TemplateDocumentContextGS {
    let context = templateContexts.get(targetDocument);
    if (!context) {
        context = {
            templates: new Map<string, HTMLElement>(),
            initializedFromDocument: false
        };
        templateContexts.set(targetDocument, context);
    }
    return context;
}

function getTemplateName(element: Element): string {
    return normalizeTemplateName(
        element.getAttribute('gs-' + templateMetadataGS.templateName) ?? undefined
    );
}

function findTemplateElement(targetDocument: Document, selector: string, name: string): HTMLElement | undefined {
    for (const element of Array.from(targetDocument.querySelectorAll(selector))) {
        if (getTemplateName(element) === name) return element as HTMLElement;
    }
    return undefined;
}

/** @internal */
export function registerTemplateConstructor<T extends object>(
    name: string,
    constructor: TemplateConstructorGS<T>
): void {
    const key = normalizeTemplateName(name);
    if (!key) throw new Error('Template constructor name must not be empty.');
    const previous = templateConstructors.get(key);
    if (previous && previous !== constructor)
        throw new Error(`Template constructor '${name}' is already registered.`);
    templateConstructors.set(key, constructor);
}

/** @internal */
export function unregisterTemplateConstructor(name: string): void {
    templateConstructors.delete(normalizeTemplateName(name));
}

/** @internal */
export function getTemplateConstructor<T extends object>(name: string): TemplateConstructorGS<T> | undefined {
    return templateConstructors.get(normalizeTemplateName(name)) as TemplateConstructorGS<T> | undefined;
}

/** @internal */
export function registerTemplatePack(
    html: string,
    buildId?: string,
    targetDocument: Document = RuntimeGS.document
): void {
    const context = getTemplateContext(targetDocument);
    const holder = targetDocument.createElement('div');
    holder.innerHTML = html;
    const registrations = new Map<string, HTMLElement>();
    holder.querySelectorAll('template').forEach(element => {
        const name = getTemplateName(element);
        if (!name) return;
        if (registrations.has(name))
            throw new Error(`Template '${name}' is duplicated in the loaded GS template pack.`);
        registrations.set(name, element as HTMLElement);
    });
    registrations.forEach((template, name) => context.templates.set(name, template));
    if (buildId) context.buildId = buildId;
}

/** @internal */
export function tryGetTemplateElement(
    templateName: string,
    templateClassName: string,
    targetDocument: Document = RuntimeGS.document
): HTMLElement | undefined {
    const key = normalizeTemplateName(templateName);
    if (!key) return undefined;
    const context = getTemplateContext(targetDocument);

    if (!context.initializedFromDocument) {
        targetDocument.querySelectorAll('template').forEach(element => {
            const name = getTemplateName(element);
            if (name) context.templates.set(name, element as HTMLElement);
        });
        context.initializedFromDocument = true;
    }

    let element = context.templates.get(key);
    if (!element) element = findTemplateElement(targetDocument, 'template', key);
    if (!element) element = findTemplateElement(targetDocument, `.${templateClassName}`, key);
    if (!element) return undefined;
    context.templates.set(key, element);
    return element;
}

/** @internal */
export function getTemplateBuildId(targetDocument: Document = RuntimeGS.document): string | undefined {
    return getTemplateContext(targetDocument).buildId;
}

/** @internal */
export function clearTemplateDocumentCache(targetDocument: Document = RuntimeGS.document): void {
    templateContexts.delete(targetDocument);
}
